import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { API_BASE, fallbackServices as fallbackSvc } from '../data/fallback';

/* ── Inline SVG Illustrations for each service ── */
const WebDevIllustration = () => (
  <svg viewBox="0 0 340 200" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
    <defs>
      <linearGradient id="wdg1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.9" />
      </linearGradient>
      <linearGradient id="wdg2" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6" />
        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.2" />
      </linearGradient>
    </defs>
    <rect width="340" height="200" fill="url(#wdg1)" />
    {/* Grid dots */}
    {[0,1,2,3,4,5,6,7,8].map(i => [0,1,2,3].map(j => (
      <circle key={`${i}-${j}`} cx={20+i*38} cy={20+j*48} r="1.5" fill="rgba(255,255,255,0.15)" />
    )))}
    {/* Browser window */}
    <rect x="40" y="28" width="180" height="120" rx="10" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
    <rect x="40" y="28" width="180" height="24" rx="10" fill="rgba(255,255,255,0.2)" />
    <circle cx="56" cy="40" r="4" fill="rgba(255,120,100,0.8)" />
    <circle cx="70" cy="40" r="4" fill="rgba(255,200,60,0.8)" />
    <circle cx="84" cy="40" r="4" fill="rgba(80,200,100,0.8)" />
    <rect x="100" y="34" width="96" height="12" rx="6" fill="rgba(255,255,255,0.15)" />
    {/* Code lines */}
    <rect x="52" y="64" width="40" height="6" rx="3" fill="#60a5fa" opacity="0.9" />
    <rect x="52" y="78" width="130" height="5" rx="2.5" fill="rgba(255,255,255,0.3)" />
    <rect x="52" y="90" width="100" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
    <rect x="52" y="102" width="120" height="5" rx="2.5" fill="rgba(255,255,255,0.25)" />
    <rect x="52" y="114" width="80" height="5" rx="2.5" fill="#93c5fd" opacity="0.7" />
    <rect x="52" y="126" width="60" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
    {/* Floating card */}
    <rect x="190" y="60" width="110" height="70" rx="10" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.2">
      <animate attributeName="y" values="60;52;60" dur="4s" repeatCount="indefinite" />
    </rect>
    <rect x="202" y="72" width="50" height="7" rx="3.5" fill="rgba(255,255,255,0.6)" />
    <rect x="202" y="86" width="80" height="5" rx="2.5" fill="rgba(255,255,255,0.3)" />
    <rect x="202" y="96" width="60" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
    <rect x="202" y="112" width="44" height="12" rx="6" fill="#3b82f6" />
    <text x="214" y="122" fill="white" fontSize="7" fontWeight="700">Launch</text>
    {/* Animated cursor */}
    <rect x="152" y="90" width="2" height="12" rx="1" fill="#60a5fa">
      <animate attributeName="opacity" values="1;0;1" dur="1.2s" repeatCount="indefinite" />
    </rect>
    {/* Glow orb */}
    <circle cx="290" cy="40" r="40" fill="radial-gradient(circle, rgba(96,165,250,0.3), transparent)" opacity="0.3">
      <animate attributeName="r" values="40;50;40" dur="5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const DesignIllustration = () => (
  <svg viewBox="0 0 340 200" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
    <defs>
      <linearGradient id="dig1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#6d28d9" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    <rect width="340" height="200" fill="url(#dig1)" />
    {/* Canvas */}
    <rect x="50" y="25" width="155" height="145" rx="10" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    {/* Color palette swatches */}
    {['#f472b6','#a78bfa','#60a5fa','#34d399','#fbbf24','#f87171'].map((c, i) => (
      <rect key={i} x={62 + i * 22} y="34" width="16" height="16" rx="4" fill={c} opacity="0.9" />
    ))}
    {/* Wireframe elements */}
    <rect x="62" y="62" width="130" height="50" rx="6" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="4 2" />
    <line x1="62" y1="62" x2="192" y2="112" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
    <line x1="192" y1="62" x2="62" y2="112" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
    <rect x="62" y="122" width="60" height="8" rx="4" fill="rgba(255,255,255,0.4)" />
    <rect x="62" y="136" width="90" height="6" rx="3" fill="rgba(255,255,255,0.2)" />
    <rect x="62" y="148" width="75" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
    {/* Pen tool floating */}
    <g transform="translate(210,50)">
      <rect width="90" height="110" rx="12" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2">
        <animate attributeName="y" values="0;-8;0" dur="4s" repeatCount="indefinite" />
      </rect>
      <circle cx="45" cy="35" r="22" fill="rgba(167,139,250,0.4)" stroke="rgba(167,139,250,0.6)" strokeWidth="1.5" />
      <path d="M38 35 L45 26 L52 35 L45 38 Z" fill="rgba(255,255,255,0.8)" />
      <rect x="14" y="66" width="62" height="6" rx="3" fill="rgba(255,255,255,0.4)" />
      <rect x="14" y="78" width="44" height="5" rx="2.5" fill="rgba(255,255,255,0.2)" />
      <rect x="14" y="90" width="52" height="5" rx="2.5" fill="rgba(255,255,255,0.15)" />
    </g>
    {/* Decorative circles */}
    <circle cx="30" cy="30" r="20" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" strokeDasharray="3 4" >
      <animateTransform attributeName="transform" type="rotate" from="0 30 30" to="360 30 30" dur="15s" repeatCount="indefinite" />
    </circle>
  </svg>
);

const DigitalMarketingIllustration = () => (
  <svg viewBox="0 0 340 200" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
    <defs>
      <linearGradient id="dmg1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#10b981" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#059669" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    <rect width="340" height="200" fill="url(#dmg1)" />
    {/* Chart bars */}
    {[55, 80, 45, 95, 65, 110, 75, 130].map((h, i) => (
      <g key={i}>
        <rect x={28 + i * 30} y={160 - h} width="20" height={h} rx="4"
          fill="rgba(255,255,255,0.25)" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
        <rect x={28 + i * 30} y={160 - h} width="20" height={h * 0.3} rx="4"
          fill="rgba(255,255,255,0.4)" />
      </g>
    ))}
    {/* Trend line */}
    <polyline points="38,105 68,80 98,115 128,65 158,95 188,50 218,75 248,30"
      fill="none" stroke="#6ee7b7" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="400" strokeDashoffset="0">
      <animate attributeName="stroke-dashoffset" from="400" to="0" dur="2s" fill="freeze" />
    </polyline>
    {/* Data points */}
    {[[38,105],[68,80],[98,115],[128,65],[158,95],[188,50],[218,75],[248,30]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="4" fill="#34d399" stroke="white" strokeWidth="1.5">
        <animate attributeName="r" values="4;6;4" dur={`${2+i*0.3}s`} repeatCount="indefinite" />
      </circle>
    ))}
    {/* Floating metric card */}
    <rect x="220" y="25" width="108" height="70" rx="12" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.2">
      <animate attributeName="y" values="25;16;25" dur="4s" repeatCount="indefinite" />
    </rect>
    <text x="234" y="46" fill="rgba(255,255,255,0.5)" fontSize="8" fontWeight="600">ROI BOOST</text>
    <text x="234" y="64" fill="white" fontSize="22" fontWeight="800">+240%</text>
    <rect x="234" y="74" width="80" height="12" rx="6" fill="rgba(52,211,153,0.4)" />
    <rect x="234" y="74" width="60" height="12" rx="6" fill="rgba(52,211,153,0.7)" />
    {/* X axis line */}
    <line x1="20" y1="162" x2="260" y2="162" stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
  </svg>
);

const SEOIllustration = () => (
  <svg viewBox="0 0 340 200" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
    <defs>
      <linearGradient id="seog1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#d97706" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    <rect width="340" height="200" fill="url(#seog1)" />
    {/* Search bar */}
    <rect x="30" y="22" width="200" height="32" rx="16" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.35)" strokeWidth="1.5" />
    <circle cx="52" cy="38" r="8" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" />
    <line x1="58" y1="44" x2="64" y2="50" stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" />
    <rect x="74" y="33" width="100" height="10" rx="5" fill="rgba(255,255,255,0.35)">
      <animate attributeName="width" values="100;120;100" dur="3s" repeatCount="indefinite" />
    </rect>
    {/* Rank results */}
    {[
      { rank: '#1', w: 155, col: '#fef3c7', bar: 155 },
      { rank: '#2', w: 120, col: 'rgba(255,255,255,0.4)', bar: 120 },
      { rank: '#3', w: 90,  col: 'rgba(255,255,255,0.25)', bar: 90 },
    ].map((r, i) => (
      <g key={i}>
        <rect x="30" y={70 + i * 36} width="200" height="28" rx="7"
          fill={i === 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)'}
          stroke={i === 0 ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)'} strokeWidth="1" />
        <text x="44" y={89 + i * 36} fill={i === 0 ? '#fef3c7' : 'rgba(255,255,255,0.5)'}
          fontSize="9" fontWeight="800">{r.rank}</text>
        <rect x="62" y={78 + i * 36} width={r.bar} height="6" rx="3"
          fill={i === 0 ? 'rgba(254,243,199,0.6)' : 'rgba(255,255,255,0.2)'} />
        <rect x="62" y={88 + i * 36} width={r.bar * 0.7} height="4" rx="2"
          fill="rgba(255,255,255,0.15)" />
      </g>
    ))}
    {/* Trophy */}
    <g transform="translate(248,18)">
      <rect width="80" height="88" rx="12" fill="rgba(255,255,255,0.15)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2">
        <animate attributeName="y" values="0;-8;0" dur="4.5s" repeatCount="indefinite" />
      </rect>
      <text x="22" y="44" fontSize="28">🏆</text>
      <text x="16" y="62" fill="white" fontSize="11" fontWeight="800">TOP #1</text>
      <rect x="12" y="68" width="56" height="6" rx="3" fill="rgba(255,255,255,0.3)" />
    </g>
    {/* Keywords floating */}
    {['SEO','Rank','Traffic','CTR'].map((kw, i) => (
      <text key={i} x={30 + i * 55} y="182" fill="rgba(255,255,255,0.2)" fontSize="10" fontWeight="700">{kw}</text>
    ))}
  </svg>
);

const SMOIllustration = () => (
  <svg viewBox="0 0 340 200" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
    <defs>
      <linearGradient id="smog1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#0891b2" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    <rect width="340" height="200" fill="url(#smog1)" />
    {/* Profile card */}
    <rect x="25" y="20" width="130" height="155" rx="14" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    <rect x="25" y="20" width="130" height="60" rx="14" fill="rgba(255,255,255,0.15)" />
    <circle cx="90" cy="64" r="24" fill="rgba(6,182,212,0.5)" stroke="white" strokeWidth="2.5" />
    <text x="82" y="70" fill="white" fontSize="18">👤</text>
    <text x="78" y="98" fill="white" fontSize="9" fontWeight="700">@YourBrand</text>
    {/* Stats row */}
    {[['1.2K','Posts'],['4.8K','Followers'],['320','Following']].map(([n,l],i) => (
      <g key={i}>
        <text x={35 + i * 40} y="118" fill="white" fontSize="9" fontWeight="800">{n}</text>
        <text x={33 + i * 40} y="130" fill="rgba(255,255,255,0.5)" fontSize="7">{l}</text>
      </g>
    ))}
    <rect x="38" y="138" width="100" height="18" rx="9" fill="rgba(255,255,255,0.2)" stroke="rgba(255,255,255,0.3)" strokeWidth="1" />
    <text x="66" y="151" fill="white" fontSize="8" fontWeight="700">✓ Verified Profile</text>
    {/* Engagement metrics */}
    <g transform="translate(170, 18)">
      {[['❤️','1.2K Likes','#f43f5e',0],['💬','340 Comments','#a78bfa',1],['🔁','890 Shares','#34d399',2],['📌','2.1K Saves','#fbbf24',3]].map(([ic,txt,col,i]) => (
        <g key={i}>
          <rect x="0" y={i * 40} width="155" height="32" rx="10"
            fill="rgba(255,255,255,0.1)" stroke="rgba(255,255,255,0.2)" strokeWidth="1">
            <animate attributeName="x" values="0;4;0" dur={`${3 + i * 0.5}s`} repeatCount="indefinite" />
          </rect>
          <text x="12" y={i * 40 + 21} fontSize="14">{ic}</text>
          <text x="34" y={i * 40 + 20} fill="white" fontSize="9" fontWeight="600">{txt}</text>
          <rect x="100" y={i * 40 + 10} width="44" height="14" rx="7" fill={`${col}55`} />
          <text x="108" y={i * 40 + 21} fill={col} fontSize="7" fontWeight="700">+28%</text>
        </g>
      ))}
    </g>
  </svg>
);

const SMMIllustration = () => (
  <svg viewBox="0 0 340 200" width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
    <defs>
      <linearGradient id="smmg1" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ec4899" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#be185d" stopOpacity="0.9" />
      </linearGradient>
    </defs>
    <rect width="340" height="200" fill="url(#smmg1)" />
    {/* Campaign card */}
    <rect x="20" y="18" width="155" height="160" rx="14" fill="rgba(255,255,255,0.12)" stroke="rgba(255,255,255,0.25)" strokeWidth="1.5" />
    <rect x="20" y="18" width="155" height="55" rx="14" fill="rgba(255,255,255,0.18)" />
    <text x="34" y="42" fill="rgba(255,255,255,0.5)" fontSize="8" fontWeight="700">CAMPAIGN</text>
    <text x="34" y="58" fill="white" fontSize="11" fontWeight="800">Summer Launch 🚀</text>
    {/* Progress bars */}
    {[['Reach','78%',0.78,'#f9a8d4'],['Clicks','54%',0.54,'#fcd34d'],['Conv.','32%',0.32,'#86efac']].map(([lbl,pct,prog,col],i) => (
      <g key={i}>
        <text x="34" y={95 + i * 32} fill="rgba(255,255,255,0.7)" fontSize="8" fontWeight="600">{lbl}</text>
        <text x={152} y={95 + i * 32} fill={col} fontSize="8" fontWeight="800" textAnchor="end">{pct}</text>
        <rect x="34" y={100 + i * 32} width="136" height="8" rx="4" fill="rgba(255,255,255,0.1)" />
        <rect x="34" y={100 + i * 32} width={136 * prog} height="8" rx="4" fill={col} opacity="0.85">
          <animate attributeName="width" from="0" to={136 * prog} dur={`${1 + i * 0.3}s`} fill="freeze" />
        </rect>
      </g>
    ))}
    <rect x="34" y="166" width="70" height="4" rx="2" fill="rgba(255,255,255,0.15)" />
    <rect x="114" y="166" width="46" height="4" rx="2" fill="rgba(249,168,212,0.4)" />
    {/* Platform icons floating */}
    {[['f','#1877f2',185,25],['in','#0a66c2',250,25],['📸','',220,70],['🐦','',185,115],['▶','#ff0000',250,115]].map(([ic,bg,x,y],i) => (
      <g key={i}>
        <rect x={x} y={y} width="45" height="45" rx="12"
          fill={bg || 'rgba(255,255,255,0.15)'} stroke="rgba(255,255,255,0.25)" strokeWidth="1">
          <animate attributeName="y" values={`${y};${y-6};${y}`} dur={`${3+i*0.7}s`} repeatCount="indefinite" />
        </rect>
        <text x={x+10} y={y+29} fill="white" fontSize={bg ? "14" : "18"} fontWeight="800">{ic}</text>
      </g>
    ))}
    {/* Scatter dots */}
    {[[305,40],[320,80],[300,130],[320,165]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="4" fill="rgba(255,255,255,0.2)">
        <animate attributeName="r" values="4;7;4" dur={`${2+i}s`} repeatCount="indefinite" />
      </circle>
    ))}
  </svg>
);

/* ── Static service images for fallback ── */
const serviceImages = {
  'Website Development':       <WebDevIllustration />,
  'Website Design':            <DesignIllustration />,
  'Digital Marketing':         <DigitalMarketingIllustration />,
  'SEO':                       <SEOIllustration />,
  'Social Media Optimization': <SMOIllustration />,
  'Social Media Marketing':    <SMMIllustration />,
};

const serviceColors = {
  'Website Development':       { from: '#3b82f6', to: '#1d4ed8', tag: '#93c5fd' },
  'Website Design':            { from: '#8b5cf6', to: '#6d28d9', tag: '#c4b5fd' },
  'Digital Marketing':         { from: '#10b981', to: '#059669', tag: '#6ee7b7' },
  'SEO':                       { from: '#f59e0b', to: '#d97706', tag: '#fcd34d' },
  'Social Media Optimization': { from: '#06b6d4', to: '#0891b2', tag: '#67e8f9' },
  'Social Media Marketing':    { from: '#ec4899', to: '#be185d', tag: '#f9a8d4' },
};

const fallbackColorMap = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#06b6d4','#ec4899'];

/* ── Rich service descriptions ── */
const serviceDetails = {
  'Website Development': {
    shortDesc: 'High-performance websites engineered for speed, security & scale.',
    desc: 'We build lightning-fast, SEO-optimized websites using the latest frameworks — React, Node.js, PHP, and more. Every project is tailored to your business goals with clean code, mobile-first design, and lifetime support.',
    features: ['Custom Web Applications','Responsive Design','API Integration','CMS & Admin Panel','Performance Optimization','SSL & Security Setup','E-Commerce Development','Lifetime Support'],
    tag: 'Engineering',
  },
  'Website Design': {
    shortDesc: 'Pixel-perfect UI/UX that converts visitors into customers.',
    desc: 'Our designers craft visually stunning interfaces that balance aesthetics with usability. From wireframes to final prototypes, we ensure your brand looks exceptional on every screen — desktop, tablet, and mobile.',
    features: ['UI/UX Strategy & Research','Brand Identity Design','Wireframes & Prototypes','Figma / Adobe XD Design','Landing Page Design','Logo & Brand Kit','Motion & Animation','Design System Creation'],
    tag: 'Design',
  },
  'Digital Marketing': {
    shortDesc: 'Data-driven campaigns that grow your revenue, not just your traffic.',
    desc: 'We execute multi-channel digital marketing strategies — Google Ads, content marketing, email campaigns and more — all tied to measurable ROI. Every decision is backed by analytics, not guesswork.',
    features: ['Google Ads (PPC)','Content Marketing','Email Campaigns','Conversion Rate Optimization','Analytics & Reporting','Marketing Automation','Competitor Analysis','Monthly Growth Reports'],
    tag: 'Growth',
  },
  'SEO': {
    shortDesc: 'Rank #1 on Google and dominate your local market.',
    desc: 'Our SEO specialists use white-hat techniques to build lasting organic rankings. From on-page optimization and technical audits to high-authority link building — we get you to the top and keep you there.',
    features: ['Technical SEO Audit','Keyword Research','On-Page Optimization','Local SEO & GMB','Link Building','Core Web Vitals Fix','Schema Markup','Monthly Rank Reports'],
    tag: 'Search',
  },
  'Social Media Optimization': {
    shortDesc: 'Transform your profiles into powerful brand assets.',
    desc: 'We optimize every aspect of your social media presence — profile completeness, content strategy, hashtag research, audience targeting and engagement loops — turning your pages into high-converting brand channels.',
    features: ['Profile Audit & Optimization','Hashtag Strategy','Content Calendar Planning','Audience Growth Tactics','Bio & Cover Design','Story Highlights Setup','Competitor Benchmarking','Engagement Rate Boost'],
    tag: 'Social',
  },
  'Social Media Marketing': {
    shortDesc: 'Campaigns that build communities and drive real sales.',
    desc: 'From Instagram reels to Facebook ads and LinkedIn campaigns — we create thumb-stopping content and targeted paid campaigns that build loyal communities, generate quality leads and drive measurable revenue.',
    features: ['Paid Social Campaigns','Instagram & Facebook Ads','Reels & Video Content','Influencer Collaborations','Community Management','Lead Generation Ads','A/B Testing','Performance Dashboard'],
    tag: 'Marketing',
  },
};

/* ── Process steps ── */
const processSteps = [
  { step: '01', icon: '🔍', title: 'Discovery & Planning',   desc: 'Deep-dive into your business goals, audience, and competitors to craft a precise strategy.' },
  { step: '02', icon: '🎨', title: 'Design & Prototype',     desc: 'Stunning mockups and interactive prototypes reviewed and approved by you before code.' },
  { step: '03', icon: '⚙️', title: 'Development',            desc: 'Built with the latest tech stack — clean, optimized, accessible and future-proof.' },
  { step: '04', icon: '🧪', title: 'Testing & QA',           desc: 'Rigorous cross-browser, cross-device testing to ensure zero bugs at launch.' },
  { step: '05', icon: '🚀', title: 'Launch & Deploy',         desc: 'Secure, fast deployment on enterprise-grade infrastructure with 99.9% uptime.' },
  { step: '06', icon: '📊', title: 'Growth & Support',        desc: 'Ongoing maintenance, performance monitoring and continuous improvement post-launch.' },
];

/* ── Why Us stats ── */
const whyUs = [
  { icon: '⚡', num: '48h', label: 'Average Turnaround', desc: 'From brief to first design in under 48 hours' },
  { icon: '🌟', num: '5★', label: 'Client Rating',       desc: 'Consistently rated 5 stars by 50+ clients' },
  { icon: '🛡️', num: '100%', label: 'Satisfaction',     desc: 'Full refund if you\'re not satisfied' },
  { icon: '📞', num: '24/7', label: 'Support',           desc: 'Round-the-clock technical support' },
];

/* ── Floating particle component ── */
const FloatingParticles = () => (
  <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
    {[...Array(20)].map((_, i) => (
      <div key={i} style={{
        position: 'absolute',
        width: `${2 + (i % 4)}px`,
        height: `${2 + (i % 4)}px`,
        borderRadius: '50%',
        background: i % 3 === 0 ? 'rgba(232,96,58,0.4)' : i % 3 === 1 ? 'rgba(79,195,247,0.3)' : 'rgba(255,255,255,0.15)',
        left: `${5 + (i * 4.7) % 92}%`,
        top: `${10 + (i * 7.3) % 80}%`,
        animation: `particleFloat${i % 3} ${4 + (i % 5)}s ease-in-out infinite`,
        animationDelay: `${(i * 0.4) % 3}s`,
      }} />
    ))}
  </div>
);

/* ── Tilt card hook ── */
const useTilt = () => {
  const ref = useRef(null);
  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 12;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -12;
    el.style.transform = `perspective(800px) rotateX(${y}deg) rotateY(${x}deg) translateY(-10px)`;
  };
  const handleLeave = () => {
    if (ref.current) ref.current.style.transform = 'perspective(800px) rotateX(0) rotateY(0) translateY(0)';
  };
  return { ref, onMouseMove: handleMove, onMouseLeave: handleLeave };
};

/* ── Single Service Card ── */
const ServiceCard = ({ service, index, colors }) => {
  const tilt = useTilt();
  const details = serviceDetails[service.title] || {};
  const features = details.features || (service.features || []);
  const tag = details.tag || service.tag || 'Service';
  const desc = details.desc || service.description || '';
  const shortDesc = details.shortDesc || service.shortDesc || service.description || '';
  const Illustration = serviceImages[service.title];

  return (
    <AnimatedSection animation="reveal" delay={index * 80}>
      <div className="svc-card" {...tilt} style={{ '--card-from': colors.from, '--card-to': colors.to }}>
        {/* Image / illustration header */}
        <div className="svc-card-img">
          {Illustration
            ? Illustration
            : <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${colors.from}, ${colors.to})` }} />
          }
          {/* Overlay gradient at bottom for readability */}
          <div className="svc-img-overlay" />
          {/* Tag pill */}
          <div className="svc-tag" style={{ background: `${colors.tag}22`, border: `1px solid ${colors.tag}55`, color: colors.tag }}>
            {tag}
          </div>
          {/* Icon + Title over image */}
          <div className="svc-img-title">
            <div className="svc-icon-wrap">
              <span className="svc-emoji">{service.icon}</span>
            </div>
            <h3 className="svc-title">{service.title}</h3>
            <p className="svc-short">{shortDesc}</p>
          </div>
        </div>

        {/* Card body */}
        <div className="svc-body">
          <p className="svc-desc">{desc}</p>
          <ul className="svc-features">
            {features.slice(0, 8).map((f, fi) => (
              <li key={fi}>
                <span className="svc-check" style={{ background: `${colors.from}22`, color: colors.from }}>✓</span>
                {typeof f === 'string' ? f : f.text}
              </li>
            ))}
          </ul>
          <Link to="/contact#quote" className="svc-cta-btn" style={{ background: `linear-gradient(135deg, ${colors.from}, ${colors.to})` }}>
            Get Started
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </div>
    </AnimatedSection>
  );
};

/* ── Main Component ── */
const Services = () => {
  const [services, setServices] = useState(fallbackSvc);
  const [activeFilter, setActiveFilter] = useState('All');

  useEffect(() => {
    fetch(`${API_BASE}/api/services`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { if (d.success && d.data.length) setServices(d.data); })
      .catch(() => setServices(fallbackSvc));
  }, []);

  const categories = ['All', ...new Set(services.map(s => serviceDetails[s.title]?.tag || 'Service'))];
  const filtered = activeFilter === 'All' ? services : services.filter(s => (serviceDetails[s.title]?.tag || 'Service') === activeFilter);

  return (
    <div>
      <style>{`
        /* ── Particle keyframes ── */
        @keyframes particleFloat0 { 0%,100%{transform:translateY(0) translateX(0)} 50%{transform:translateY(-20px) translateX(8px)} }
        @keyframes particleFloat1 { 0%,100%{transform:translateY(0) translateX(0)} 50%{transform:translateY(-14px) translateX(-10px)} }
        @keyframes particleFloat2 { 0%,100%{transform:translateY(0) translateX(0)} 50%{transform:translateY(-18px) translateX(5px)} }

        /* ── Hero ── */
        .svc-hero {
          position: relative;
          background: linear-gradient(135deg, #050516 0%, #0d0d2b 50%, #0a0a1e 100%);
          padding: 140px 0 100px;
          text-align: center;
          overflow: hidden;
        }
        .svc-hero-grid {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 48px 48px;
          pointer-events: none;
        }
        .svc-hero-orb1 {
          position: absolute; width: 600px; height: 600px; border-radius: 50%;
          background: radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%);
          top: -200px; left: -150px; pointer-events: none;
          animation: orbDrift 14s ease-in-out infinite;
        }
        .svc-hero-orb2 {
          position: absolute; width: 500px; height: 500px; border-radius: 50%;
          background: radial-gradient(circle, rgba(232,96,58,0.1) 0%, transparent 70%);
          bottom: -150px; right: -100px; pointer-events: none;
          animation: orbDrift 18s ease-in-out infinite reverse;
        }
        .svc-hero-orb3 {
          position: absolute; width: 300px; height: 300px; border-radius: 50%;
          background: radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%);
          top: 20%; right: 20%; pointer-events: none;
          animation: orbDrift 10s ease-in-out infinite 2s;
        }
        .svc-hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(232,96,58,0.1); border: 1px solid rgba(232,96,58,0.3);
          color: #f97316; padding: 8px 20px; border-radius: 9999px;
          font-size: 0.72rem; font-weight: 700; letter-spacing: 0.12em;
          text-transform: uppercase; margin-bottom: 24px;
          animation: fadeInDown 0.8s ease both;
        }
        .svc-hero-title {
          font-size: clamp(2.2rem, 5vw, 4rem); font-weight: 900;
          color: white; line-height: 1.08; letter-spacing: -0.04em;
          margin-bottom: 22px; position: relative; z-index: 1;
          animation: fadeInUp 0.9s ease 0.1s both;
        }
        .svc-hero-title span {
          background: linear-gradient(135deg, #f97316, #ec4899);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .svc-hero-sub {
          font-size: 1.1rem; color: rgba(255,255,255,0.55); max-width: 560px;
          margin: 0 auto 36px; line-height: 1.85; font-weight: 400;
          animation: fadeInUp 0.9s ease 0.2s both;
        }
        .svc-hero-pills {
          display: flex; align-items: center; justify-content: center;
          gap: 10px; flex-wrap: wrap; margin-bottom: 36px;
          animation: fadeInUp 0.9s ease 0.3s both;
        }
        .svc-hero-pill {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.7); padding: 7px 16px; border-radius: 9999px;
          font-size: 0.8rem; font-weight: 600;
        }
        .svc-hero-pill .dot { width: 7px; height: 7px; border-radius: 50%; }
        .svc-breadcrumb {
          display: flex; align-items: center; justify-content: center; gap: 8px;
          font-size: 0.82rem; color: rgba(255,255,255,0.35); margin-top: 8px;
          animation: fadeInUp 0.9s ease 0.35s both;
        }
        .svc-breadcrumb a { color: rgba(79,195,247,0.7); }

        /* ── Stats bar ── */
        .svc-stats-bar {
          background: white; border-bottom: 1px solid #e9ecef;
          padding: 28px 0; box-shadow: 0 4px 24px rgba(0,0,0,0.06);
        }
        .svc-stats-inner {
          display: flex; align-items: center; justify-content: space-around;
          flex-wrap: wrap; gap: 20px;
        }
        .svc-stat-item { text-align: center; }
        .svc-stat-num {
          font-size: 1.8rem; font-weight: 900; color: #16165a;
          letter-spacing: -0.04em; line-height: 1;
        }
        .svc-stat-num span { color: #e8603a; }
        .svc-stat-lbl { font-size: 0.75rem; color: #6e6e66; font-weight: 600; margin-top: 4px; letter-spacing: 0.06em; text-transform: uppercase; }
        .svc-stat-div { width: 1px; height: 40px; background: #e4e4e0; }

        /* ── Filter tabs ── */
        .svc-filters {
          display: flex; align-items: center; justify-content: center;
          gap: 8px; flex-wrap: wrap; margin-bottom: 52px;
        }
        .svc-filter-btn {
          padding: 9px 22px; border-radius: 9999px; font-size: 0.82rem; font-weight: 700;
          cursor: pointer; transition: all 0.25s; border: 1.5px solid #e4e4e0;
          background: white; color: #6e6e66; letter-spacing: 0.02em;
        }
        .svc-filter-btn:hover { border-color: #16165a; color: #16165a; transform: translateY(-1px); }
        .svc-filter-btn.active {
          background: linear-gradient(135deg, #16165a, #252580);
          color: white; border-color: transparent;
          box-shadow: 0 4px 16px rgba(22,22,90,0.3);
        }

        /* ── Cards grid ── */
        .svc-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 28px;
        }
        .svc-card {
          background: white;
          border-radius: 22px;
          overflow: hidden;
          border: 1px solid #e9ecef;
          transition: all 0.45s cubic-bezier(0.34,1.56,0.64,1);
          box-shadow: 0 4px 16px rgba(0,0,0,0.05);
          will-change: transform;
        }
        .svc-card:hover {
          box-shadow: 0 28px 60px rgba(0,0,0,0.14);
          border-color: transparent;
        }

        /* ── Card image header ── */
        .svc-card-img {
          position: relative;
          height: 200px;
          overflow: hidden;
        }
        .svc-img-overlay {
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, transparent 20%, rgba(0,0,0,0.55) 100%);
          z-index: 2;
        }
        .svc-tag {
          position: absolute; top: 14px; right: 14px; z-index: 3;
          padding: 4px 12px; border-radius: 9999px;
          font-size: 0.68rem; font-weight: 800; letter-spacing: 0.1em; text-transform: uppercase;
        }
        .svc-img-title {
          position: absolute; bottom: 0; left: 0; right: 0;
          padding: 16px 20px; z-index: 3;
        }
        .svc-icon-wrap {
          width: 44px; height: 44px; border-radius: 12px;
          background: rgba(255,255,255,0.15); backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.25);
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 8px;
        }
        .svc-emoji { font-size: 1.5rem; line-height: 1; }
        .svc-title { font-size: 1.1rem; font-weight: 800; color: white; margin-bottom: 4px; letter-spacing: -0.02em; }
        .svc-short { font-size: 0.78rem; color: rgba(255,255,255,0.75); line-height: 1.5; }

        /* ── Card body ── */
        .svc-body { padding: 22px 22px 20px; }
        .svc-desc {
          font-size: 0.84rem; color: #5a5a52; line-height: 1.82;
          margin-bottom: 18px; border-bottom: 1px solid #f2f2ef; padding-bottom: 18px;
        }
        .svc-features {
          list-style: none; padding: 0; margin: 0 0 20px;
          display: grid; grid-template-columns: 1fr 1fr; gap: 7px;
        }
        .svc-features li {
          display: flex; align-items: center; gap: 7px;
          font-size: 0.78rem; color: #3d3d38; font-weight: 500; line-height: 1.3;
        }
        .svc-check {
          width: 18px; height: 18px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 0.6rem; font-weight: 800; flex-shrink: 0;
        }
        .svc-cta-btn {
          display: flex; align-items: center; justify-content: center; gap: 7px;
          width: 100%; padding: 11px 20px; border-radius: 10px;
          color: white; font-size: 0.85rem; font-weight: 700; text-decoration: none;
          transition: all 0.25s; box-shadow: 0 4px 14px rgba(0,0,0,0.2);
        }
        .svc-cta-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.25); filter: brightness(1.08); }

        /* ── Process section ── */
        .svc-process {
          background: linear-gradient(160deg, #050516 0%, #0a0a2e 50%, #0d0d1e 100%);
          padding: 100px 0; position: relative; overflow: hidden;
        }
        .svc-process-grid {
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px;
        }
        .svc-process-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 18px; padding: 32px 26px;
          position: relative; overflow: hidden;
          transition: all 0.4s ease;
        }
        .svc-process-card::before {
          content: ''; position: absolute; inset: 0; border-radius: 18px;
          background: linear-gradient(135deg, rgba(255,255,255,0.06) 0%, transparent 100%);
          opacity: 0; transition: opacity 0.4s;
        }
        .svc-process-card:hover { transform: translateY(-6px); border-color: rgba(232,96,58,0.25); background: rgba(255,255,255,0.07); }
        .svc-process-card:hover::before { opacity: 1; }
        .svc-process-num {
          font-size: 3rem; font-weight: 900; line-height: 1;
          background: linear-gradient(135deg, rgba(232,96,58,0.7), rgba(249,115,22,0.4));
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text; margin-bottom: 12px;
        }
        .svc-process-icon { font-size: 1.8rem; margin-bottom: 12px; }
        .svc-process-title { font-size: 1rem; font-weight: 800; color: white; margin-bottom: 10px; }
        .svc-process-desc { font-size: 0.84rem; color: rgba(255,255,255,0.5); line-height: 1.78; }
        .svc-process-connector {
          position: absolute; right: -12px; top: 50%; transform: translateY(-50%);
          width: 24px; height: 2px; background: rgba(232,96,58,0.3); z-index: 2;
        }

        /* ── Why us section ── */
        .svc-why { padding: 96px 0; background: #fafaf8; }
        .svc-why-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 22px; }
        .svc-why-card {
          background: white; border-radius: 18px; padding: 32px 24px;
          border: 1px solid #e9ecef; text-align: center;
          transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1);
          box-shadow: 0 2px 10px rgba(0,0,0,0.04);
        }
        .svc-why-card:hover { transform: translateY(-8px); box-shadow: 0 20px 50px rgba(22,22,90,0.12); border-color: transparent; }
        .svc-why-icon {
          width: 60px; height: 60px; border-radius: 16px; margin: 0 auto 16px;
          background: linear-gradient(135deg, rgba(22,22,90,0.08), rgba(37,37,128,0.06));
          display: flex; align-items: center; justify-content: center; font-size: 1.6rem;
        }
        .svc-why-num { font-size: 2.2rem; font-weight: 900; color: #16165a; letter-spacing: -0.04em; line-height: 1; margin-bottom: 6px; }
        .svc-why-label { font-size: 0.85rem; font-weight: 700; color: #222220; margin-bottom: 8px; }
        .svc-why-desc { font-size: 0.78rem; color: #6e6e66; line-height: 1.65; }

        /* ── CTA section ── */
        .svc-cta-section {
          padding: 100px 20px;
          background: linear-gradient(135deg, #16165a 0%, #1e1e6e 40%, #2d1b69 100%);
          text-align: center; position: relative; overflow: hidden;
        }
        .svc-cta-section::before {
          content: ''; position: absolute; inset: 0;
          background-image: radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 32px 32px; pointer-events: none;
        }
        .svc-cta-glow {
          position: absolute; width: 600px; height: 400px; border-radius: 50%;
          background: radial-gradient(circle, rgba(232,96,58,0.15) 0%, transparent 70%);
          top: -100px; left: 50%; transform: translateX(-50%); pointer-events: none;
        }

        /* ── Tech stack strip ── */
        .svc-tech-strip {
          background: linear-gradient(90deg, rgba(22,22,90,0.04) 0%, rgba(22,22,90,0.02) 100%);
          border-top: 1px solid #e9ecef; border-bottom: 1px solid #e9ecef;
          padding: 18px 0; overflow: hidden;
        }
        .svc-tech-scroll {
          display: flex; gap: 40px; animation: scrollLeft 22s linear infinite; white-space: nowrap;
        }
        @keyframes scrollLeft { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .svc-tech-item {
          display: flex; align-items: center; gap: 8px;
          font-size: 0.82rem; font-weight: 600; color: #6e6e66; flex-shrink: 0;
        }
        .svc-tech-dot { width: 7px; height: 7px; border-radius: 50%; background: #e8603a; flex-shrink: 0; }

        /* ── Responsive ── */
        @media (max-width: 1100px) { .svc-grid { grid-template-columns: repeat(2,1fr); } }
        @media (max-width: 900px) { .svc-process-grid { grid-template-columns: repeat(2,1fr); } .svc-why-grid { grid-template-columns: repeat(2,1fr); } }
        @media (max-width: 768px) {
          .svc-grid { grid-template-columns: 1fr; }
          .svc-process-grid { grid-template-columns: 1fr; }
          .svc-features { grid-template-columns: 1fr; }
          .svc-stats-inner .svc-stat-div { display: none; }
        }
        @media (max-width: 640px) { .svc-why-grid { grid-template-columns: repeat(2,1fr); } }
        @media (max-width: 480px) { .svc-why-grid { grid-template-columns: 1fr; } }
      `}</style>

      {/* ── HERO ── */}
      <div className="svc-hero">
        <div className="svc-hero-grid" />
        <div className="svc-hero-orb1" />
        <div className="svc-hero-orb2" />
        <div className="svc-hero-orb3" />
        <FloatingParticles />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="svc-hero-badge">
            <svg viewBox="0 0 24 24" width="11" height="11" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            Trusted by 50+ Businesses Since 2021
          </div>
          <h1 className="svc-hero-title">
            Digital Services That<br />
            <span>Actually Grow</span> Your Business
          </h1>
          <p className="svc-hero-sub">
            From pixel-perfect designs to performance-driven marketing — everything your business needs to win online, under one roof.
          </p>
          <div className="svc-hero-pills">
            {[
              { dot: '#3b82f6', label: 'Website Development' },
              { dot: '#8b5cf6', label: 'UI/UX Design' },
              { dot: '#10b981', label: 'Digital Marketing' },
              { dot: '#f59e0b', label: 'SEO' },
              { dot: '#ec4899', label: 'Social Media' },
            ].map((p, i) => (
              <div key={i} className="svc-hero-pill">
                <div className="dot" style={{ background: p.dot }} />
                {p.label}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', animation: 'fadeInUp 0.9s ease 0.4s both' }}>
            <Link to="/contact#quote" className="btn btn-primary" style={{ fontSize: '0.95rem', padding: '14px 32px' }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
              Get Free Consultation
            </Link>
            <Link to="/portfolio" className="btn btn-secondary" style={{ fontSize: '0.95rem', padding: '14px 32px' }}>
              View Our Work →
            </Link>
          </div>
          <div className="svc-breadcrumb">
            <Link to="/">Home</Link><span>›</span><span>Services</span>
          </div>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div className="svc-stats-bar">
        <div className="container">
          <div className="svc-stats-inner">
            {[
              { num: '200', suf: '+', label: 'Projects Delivered' },
              { num: '50',  suf: '+', label: 'Happy Clients' },
              { num: '5',   suf: '★', label: 'Average Rating' },
              { num: '48',  suf: 'h', label: 'Avg. Turnaround' },
              { num: '100', suf: '%', label: 'Satisfaction Rate' },
            ].map((s, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div className="svc-stat-div" />}
                <div className="svc-stat-item">
                  <div className="svc-stat-num">{s.num}<span>{s.suf}</span></div>
                  <div className="svc-stat-lbl">{s.label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── TECH STRIP ── */}
      <div className="svc-tech-strip">
        <div className="svc-tech-scroll">
          {['React','Node.js','PHP','Laravel','WordPress','MySQL','MongoDB','Flutter','React Native','Python','Django','Figma',
            'Google Ads','Meta Ads','SEO','Analytics','Shopify','AWS',
            'React','Node.js','PHP','Laravel','WordPress','MySQL','MongoDB','Flutter','React Native','Python','Django','Figma',
            'Google Ads','Meta Ads','SEO','Analytics','Shopify','AWS'].map((tech, i) => (
            <div key={i} className="svc-tech-item">
              <div className="svc-tech-dot" />
              {tech}
            </div>
          ))}
        </div>
      </div>

      {/* ── SERVICES GRID ── */}
      <section className="section-padding" style={{ background: '#fafaf8' }}>
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">What We Offer</span>
            <h2 className="section-title">Our <span>Expert Services</span></h2>
            <div className="section-divider"><span /><span /><span /></div>
            <p className="section-subtitle">
              End-to-end digital solutions crafted by specialists. We don't just deliver work — we deliver results you can measure.
            </p>
          </AnimatedSection>

          {/* Filter tabs */}
          <div className="svc-filters">
            {categories.map(cat => (
              <button key={cat} className={`svc-filter-btn${activeFilter === cat ? ' active' : ''}`}
                onClick={() => setActiveFilter(cat)}>
                {cat}
              </button>
            ))}
          </div>

          <div className="svc-grid">
            {filtered.map((s, i) => {
              const colors = serviceColors[s.title] || {
                from: fallbackColorMap[i % fallbackColorMap.length],
                to:   fallbackColorMap[i % fallbackColorMap.length] + 'cc',
                tag:  fallbackColorMap[i % fallbackColorMap.length] + '99',
              };
              return <ServiceCard key={s.id || i} service={s} index={i} colors={colors} />;
            })}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="svc-why">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">Why Saanvi Innovation</span>
            <h2 className="section-title">The <span>Saanvi Advantage</span></h2>
            <div className="section-divider"><span /><span /><span /></div>
            <p className="section-subtitle">
              We're not just a vendor — we're your long-term digital growth partner.
            </p>
          </AnimatedSection>
          <div className="svc-why-grid">
            {whyUs.map((w, i) => (
              <AnimatedSection key={i} animation="reveal" delay={i * 100}>
                <div className="svc-why-card">
                  <div className="svc-why-icon">{w.icon}</div>
                  <div className="svc-why-num">{w.num}</div>
                  <div className="svc-why-label">{w.label}</div>
                  <div className="svc-why-desc">{w.desc}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROCESS ── */}
      <section className="svc-process">
        <FloatingParticles />
        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <AnimatedSection className="section-header">
            <span className="section-tag">How We Work</span>
            <h2 className="section-title" style={{ color: 'white' }}>
              Our <span style={{ background: 'linear-gradient(135deg,#f97316,#ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>6-Step Process</span>
            </h2>
            <div className="section-divider"><span /><span /><span /></div>
            <p className="section-subtitle" style={{ color: 'rgba(255,255,255,0.5)' }}>
              A transparent, structured workflow that guarantees on-time delivery and exceptional quality every single time.
            </p>
          </AnimatedSection>
          <div className="svc-process-grid">
            {processSteps.map((p, i) => (
              <AnimatedSection key={i} animation="reveal" delay={i * 90}>
                <div className="svc-process-card">
                  <div className="svc-process-num">{p.step}</div>
                  <div className="svc-process-icon">{p.icon}</div>
                  <div className="svc-process-title">{p.title}</div>
                  <div className="svc-process-desc">{p.desc}</div>
                  {/* Connecting line (not on last of each row) */}
                  {i % 3 !== 2 && i !== processSteps.length - 1 && (
                    <div className="svc-process-connector" />
                  )}
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIAL STRIP ── */}
      <section style={{ background: 'white', padding: '60px 0', borderBottom: '1px solid #e9ecef' }}>
        <div className="container">
          <AnimatedSection>
            <div style={{ display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap', justifyContent: 'center' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: 6 }}>⭐⭐⭐⭐⭐</div>
                <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#16165a' }}>4.9 / 5.0</div>
                <div style={{ fontSize: '0.78rem', color: '#6e6e66', fontWeight: 600, marginTop: 4 }}>AVERAGE CLIENT RATING</div>
              </div>
              <div style={{ width: 1, height: 60, background: '#e4e4e0' }} />
              {[
                { text: '"Best investment for our hospital website. Delivered in 3 days!"', name: 'Global Hospital, Gwalior' },
                { text: '"Our GMB ranking went from page 3 to #1 in 30 days. Incredible!"', name: 'Local Business Owner' },
                { text: '"Clean design, fast delivery, zero headaches. Highly recommended."', name: 'Sanskar Valley School' },
              ].map((t, i) => (
                <div key={i} style={{ maxWidth: 220, textAlign: 'center' }}>
                  <div style={{ fontSize: '0.82rem', color: '#5a5a52', fontStyle: 'italic', lineHeight: 1.6, marginBottom: 8 }}>{t.text}</div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#e8603a' }}>— {t.name}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="svc-cta-section">
        <div className="svc-cta-glow" />
        <FloatingParticles />
        <AnimatedSection style={{ position: 'relative', zIndex: 1 }}>
          <span className="section-tag" style={{ marginBottom: 24 }}>Ready to Start?</span>
          <h2 style={{ fontSize: 'clamp(1.9rem, 4vw, 3.2rem)', fontWeight: 900, color: 'white', marginBottom: 18, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
            Let's Build Something <span style={{ background: 'linear-gradient(135deg,#f97316,#ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Amazing Together</span>
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', maxWidth: 560, margin: '0 auto 36px', fontSize: '1rem', lineHeight: 1.85 }}>
            Get a free consultation and custom quote for your project today. Our team responds within 2 hours — no spam, no pressure.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 36 }}>
            <Link to="/contact#quote" className="btn btn-primary" style={{ fontSize: '0.95rem', padding: '14px 34px' }}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4 }}><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
              Get Free Quote
            </Link>
            <Link to="/pricing" className="btn btn-secondary" style={{ fontSize: '0.95rem', padding: '14px 34px' }}>
              View Pricing Plans
            </Link>
          </div>
          <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
            {['✅ No Hidden Charges', '⚡ 48h Response', '🛡️ 100% Satisfaction', '📞 Free Consultation'].map((item, i) => (
              <div key={i} style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>{item}</div>
            ))}
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default Services;
